import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserLoginStatus } from '@sib/api';
import {
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sib/ui';
import { CheckCircle, NoInvitees } from '@sib/ui/icons';
import { Invitee } from '@spendbrain/types/user';
import { InviteesListItem } from './InviteesListItem';
import { InviteesListSkeleton } from './InviteesListSkeleton';

interface InviteesListProps {
  data: Invitee[];
  onRemove: (invitee: Invitee) => void;
  loading?: boolean;
}

export const InviteesList = ({
  data,
  onRemove,
  loading,
}: InviteesListProps) => {
  const { t } = useTranslation();
  const count = data.length;

  const loggedInCount = useMemo(
    () =>
      data.filter(
        invitee => invitee.userLoginStatus === UserLoginStatus.Completed
      ).length,
    [data]
  );

  const isThereAnyInvitee = count > 0;
  const hasLoggedInUsers = loggedInCount > 0;

  return (
    <div className="border-border rounded-sm border-1 shadow-[0px_4px_6px_0px_rgba(0,0,0,0.09)]">
      <div
        className={cn('border-border border-b-1 px-3.5 pt-3.5 pb-1.5', {
          'border-none': !isThereAnyInvitee,
        })}
      >
        <div className="flex items-center justify-between">
          <p className="text-primary text-sm leading-[18px] font-semibold">
            {t('components.shareDialog.invited', { count })}
          </p>
          {isThereAnyInvitee && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="text-secondary size-5" />
                    <p className="text-primary text-sm leading-[18px] font-semibold">
                      {t('components.shareDialog.loggedInCount', {
                        count: loggedInCount,
                      })}
                    </p>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {hasLoggedInUsers
                      ? t('components.shareDialog.loggedInTooltip', {
                          count: loggedInCount,
                        })
                      : t('components.shareDialog.noLoggedInUsers')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      {loading ? (
        <InviteesListSkeleton />
      ) : isThereAnyInvitee ? (
        <ul className="max-h-[250px] overflow-y-auto">
          {data.map((item, index) => (
            <InviteesListItem
              onRemove={() => onRemove(item)}
              data={item}
              key={`invitees-list-item-${index}`}
            />
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center gap-7 pt-2 pb-4">
          <NoInvitees className="text-primary" />
          <p className="text-muted-foreground text-sm font-medium">
            {t('components.shareDialog.noData')}
          </p>
        </div>
      )}
    </div>
  );
};
